'use client';

import type { ReactNode } from 'react';
import { EntityIcon, type EntityIconType } from '@terra/ui-design-system';
import { PlayCircleIcon } from '@terra/ui-icons';
import { Box, VStack, token } from '@terra/ui-styled-system';
import { formatDistanceToNowStrict } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import { useMainContentTabs } from '../../store/layoutStore';
import { useTargetType } from '../../store/projectStore';
import { useRecentItems } from '../../store/recentItemsStore';
import { formatTargetScopeKey } from '../../utils/target-scope.utils';
import { DashboardActionRow } from './DashboardActionRow';
import { DashboardCard } from './DashboardCard';
import type { RecentItemTabType } from './recent-items';

const entityIcon = (type: EntityIconType) => (
  <EntityIcon type={type} appearance="highlighted" density="compact" />
);

const executionIcon = (
  <PlayCircleIcon size={16} color={token('colors.fg.neutral.muted')} />
);

const RECENT_ITEM_ICON: Record<RecentItemTabType, ReactNode> = {
  finding: entityIcon('finding'),
  signal: entityIcon('signal'),
  executions: executionIcon,
  'agent-execution': executionIcon,
  endpoint: entityIcon('endpoint'),
  webpage: entityIcon('webPage'),
  'surface-asset': entityIcon('asset'),
  'ai-component': entityIcon('asset'),
};

const RECENT_ITEMS_EMPTY_HEIGHT = 120;

type RecentItemsCardProps = {
  applicationId: string;
};

export function RecentItemsCard({ applicationId }: RecentItemsCardProps) {
  const t = useTranslations('home.recentItems');
  const targetType = useTargetType();
  const items = useRecentItems(
    formatTargetScopeKey({ targetId: applicationId, targetType }),
  );
  const { navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();

  return (
    <DashboardCard
      title={t('title')}
      isEmpty={items.length === 0}
      emptyLabel={t('empty')}
      contentHeight={items.length === 0 ? RECENT_ITEMS_EMPTY_HEIGHT : undefined}
      flex={1}
    >
      <Box mx={-4} mb={-4}>
        <VStack gap={0} alignItems="stretch">
          {items.map((item, index) => {
            return (
              <DashboardActionRow
                key={item.key}
                icon={RECENT_ITEM_ICON[item.tab.type]}
                title={item.tab.title}
                description={t('description', {
                  kind: t(`kinds.${item.tab.type}`),
                  visited: formatDistanceToNowStrict(item.visitedAt, {
                    addSuffix: true,
                  }),
                })}
                showDivider={index > 0}
                onClick={() => navigateInActiveTab(item.tab)}
                onContextMenu={(event) =>
                  openContextMenu({
                    event,
                    items: [
                      {
                        type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
                        tab: item.tab,
                      },
                    ],
                  })
                }
              />
            );
          })}
        </VStack>
      </Box>
    </DashboardCard>
  );
}
